import { relations } from 'drizzle-orm'
import { index, pgTable, text, timestamp, uniqueIndex, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'
import z from 'zod/v4'

import { user } from '../auth'
import { Invoice } from './invoice'

/**
 * Which students the buyer chose to seat, recorded at checkout and replayed at fulfilment.
 *
 * Checkout leaves the SPA for Xendit and returns as a fresh page load, so the selection cannot
 * survive in client state. It also cannot ride on `Invoice.xenditMetadata`, which the webhook
 * overwrites wholesale (`xenditMetadata: JSON.stringify(payload)`).
 *
 * Rows are intent, not fact: a student can leave the school or be seated by a parent between
 * checkout and payment, so the fulfilment helper re-validates each child before stamping it onto
 * a minted seat rather than trusting these rows outright.
 */
export const InvoiceSeatAssignment = pgTable(
  'invoice_seat_assignment',
  {
    id: uuid().primaryKey().defaultRandom(),
    invoiceId: uuid()
      .notNull()
      .references(() => Invoice.id, { onDelete: 'cascade' }),
    childId: text()
      .notNull()
      .references(() => user.id, { onDelete: 'cascade' }),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex('invoice_seat_assignment_invoice_child_unique').on(table.invoiceId, table.childId),
    index('idx_invoice_seat_assignment_invoice').on(table.invoiceId),
  ],
)

export const invoiceSeatAssignmentRelations = relations(InvoiceSeatAssignment, ({ one }) => ({
  invoice: one(Invoice, {
    fields: [InvoiceSeatAssignment.invoiceId],
    references: [Invoice.id],
  }),

  child: one(user, {
    fields: [InvoiceSeatAssignment.childId],
    references: [user.id],
  }),
}))

export const InsertInvoiceSeatAssignmentSchema = createInsertSchema(InvoiceSeatAssignment, {
  invoiceId: z.uuid(),
  childId: z.string(),
}).omit({
  id: true,
  createdAt: true,
})

export const SelectInvoiceSeatAssignmentSchema = createSelectSchema(InvoiceSeatAssignment, {
  id: z.uuid(),
  invoiceId: z.uuid(),
  childId: z.string(),
  createdAt: z.date(),
})

export type InsertInvoiceSeatAssignment = typeof InvoiceSeatAssignment.$inferInsert
export type SelectInvoiceSeatAssignment = typeof InvoiceSeatAssignment.$inferSelect
